// src/constants/locationConstants.ts

/**
 * Location-related constants and configuration
 */

// Minimum distance (in kilometers) to consider coordinates as "significantly different"
// This determines when to use current coordinates vs stored location
// Default: 1 km (approximately 0.01 degrees)
export const LOCATION_DIFFERENCE_THRESHOLD_KM = 1.0;

// Convert km to degrees (approximate, varies by latitude)
// At equator: 1 km ≈ 0.009 degrees
// At higher latitudes: 1 km ≈ 0.009 / cos(latitude) degrees
// Using average: 0.01 degrees ≈ 1.1 km
export const LOCATION_DIFFERENCE_THRESHOLD_DEGREES = 0.01;

// Maximum age (in milliseconds) for cached location data
export const LOCATION_CACHE_MAX_AGE = 10 * 60 * 1000; // 10 minutes

// Geolocation options
export const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 15000, // 15 seconds
  maximumAge: 10000, // 10 seconds
};

/**
 * Calculate if two coordinates are significantly different
 * Uses Haversine formula for accurate distance calculation
 */
export function areCoordinatesSignificantlyDifferent(
  coord1: [number, number], // [longitude, latitude]
  coord2: [number, number], // [longitude, latitude]
  thresholdKm: number = LOCATION_DIFFERENCE_THRESHOLD_KM
): boolean {
  if (!coord1 || !coord2 || coord1.length !== 2 || coord2.length !== 2) {
    return true; // If we can't compare, assume different
  }

  const [lon1, lat1] = coord1;
  const [lon2, lat2] = coord2;

  // Validate coordinates
  if (
    typeof lon1 !== 'number' || typeof lat1 !== 'number' ||
    typeof lon2 !== 'number' || typeof lat2 !== 'number' ||
    isNaN(lon1) || isNaN(lat1) || isNaN(lon2) || isNaN(lat2)
  ) {
    return true;
  }

  // Calculate distance using Haversine formula
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distanceKm = R * c;

  return distanceKm > thresholdKm;
}


